/**
 * "Notify me at launch" block of a coming soon product (snippets/product-notify-launch.liquid).
 *
 * One piece of state, `step`, with three values:
 *   idle  the CTA and the launch date,
 *   form  an inline e-mail form in the CTA's slot,
 *   done  a confirmation card.
 *
 * Subscriptions go to the Klaviyo back-in-stock endpoint, the same one the restock alerts use
 * (component-klaviyo-backinstock.js), so a launch alert is a back-in-stock alert on a variant
 * that has never had stock. A successful sign-up is remembered per product in sessionStorage so
 * a reload within the session does not ask for the e-mail again.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class NotifyLaunch extends HTMLElement {
  connectedCallback() {
    this.steps = {
      idle: this.querySelector('[data-step="idle"]'),
      form: this.querySelector('[data-step="form"]'),
      done: this.querySelector('[data-step="done"]'),
    };
    this.cta = this.querySelector('[data-notify-open]');
    this.cancelButton = this.querySelector('[data-notify-cancel]');
    this.form = this.steps.form;
    this.input = this.form.querySelector('input[type="email"]');
    this.submitButton = this.form.querySelector('[data-notify-submit]');
    this.emailError = this.querySelector('[data-notify-error-email]');
    this.submitError = this.querySelector('[data-notify-error-submit]');
    this.storageKey = `notify-launch:${this.dataset.productId}`;

    this.cta.addEventListener('click', () => this.setStep('form'));
    this.cancelButton.addEventListener('click', () => {
      this.setStep('idle');
      this.cta.focus();
    });
    // The block sits inside the product form, so it cannot be a form of its own: submit on the
    // button and on Enter, and keep the key press away from the product form.
    this.submitButton.addEventListener('click', (event) => this.onSubmit(event));
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.onSubmit(event);
      }
    });
    this.input.addEventListener('input', () => this.showError(this.emailError, ''));

    this.setStep(this.isRemembered() ? 'done' : 'idle');
  }

  setStep(step) {
    this.step = step;

    Object.entries(this.steps).forEach(([name, element]) => {
      element.hidden = name !== step;
    });

    if (step === 'form') {
      this.input.focus();
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    const email = this.input.value.trim();

    this.showError(this.submitError, '');

    if (!EMAIL_PATTERN.test(email)) {
      this.showError(this.emailError, this.dataset.textErrorEmail);
      this.input.focus();
      return;
    }

    this.setSubmitting(true);

    try {
      await this.subscribe(email);
      this.remember();
      this.setStep('done');
    } catch (error) {
      this.showError(this.submitError, this.dataset.textErrorGeneric);
    }

    this.setSubmitting(false);
  }

  /**
   * The variant currently selected in the product form wins over the one rendered with the
   * block, so a size change made after the page load is respected.
   */
  currentVariantId() {
    const formVariant = document.querySelector('form[is="product-form"] input[name="id"]');
    return (formVariant && formVariant.value) || this.dataset.variantId;
  }

  async subscribe(email) {
    const publicApiKey = window.themeVariables.klaviyo.publicApiKey;
    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${publicApiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.currentVariantId()}`,
                },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Klaviyo back-in-stock subscription failed with status ${response.status}`);
    }
  }

  showError(element, message) {
    element.textContent = message;
    element.hidden = message === '';
  }

  setSubmitting(isSubmitting) {
    this.submitButton.disabled = isSubmitting;
    this.input.readOnly = isSubmitting;
  }

  isRemembered() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  remember() {
    try {
      window.sessionStorage.setItem(this.storageKey, '1');
    } catch (error) {
      // Private mode or blocked storage: the confirmation simply does not survive a reload.
    }
  }
}

window.customElements.define('notify-launch', NotifyLaunch);
